/*
 * The set page, kept in step with the server without leaving it.
 *
 * WHAT THIS MODULE IS ALLOWED TO DO
 * ---------------------------------
 * Remember which rows the customer dropped, how many sets they asked for and which
 * card number they typed — and then ASK THE SERVER what that costs. It never
 * multiplies, never applies a percentage and never rounds a price. Every rupee it
 * paints came out of one `splashbox.api.pricing.quote` response, which is the same
 * function `www/splashbox_set.py` renders the page from and the same one
 * `api.orders.create_order` re-runs before it books anything
 * (docs/BUILD_DECISIONS.md sections 4 and 11).
 *
 * The approved mockup did the arithmetic in JavaScript. That is exactly the defect
 * `api/pricing.py` was written to kill: three implementations of one sum are three
 * chances for the sticky bar, the ticket and the Sales Order to disagree, and the
 * customer only ever sees one of them.
 *
 * WHY A FAILURE NAVIGATES INSTEAD OF APOLOGISING
 * ----------------------------------------------
 * Every control on this page is a real submit inside `<form id="sb-set" method="get">`,
 * and the controller re-prices and re-renders the whole list from the query string.
 * So the honest fallback for a failed re-quote is not a toast — it is to do what the
 * button would have done with the script switched off: go to the URL that describes
 * the state the customer just asked for. `build_quote` runs in-process there, so a
 * rate-limited or flaky API call still ends with the right list on the screen.
 *
 * WHY THE ROWS ARE PATCHED RATHER THAN REDRAWN
 * --------------------------------------------
 * `macros.set_row` owns what a row looks like, and re-implementing it here would put
 * a second, drifting copy of the design in the bundle. What changes when a row is
 * dropped or the set count moves is small and known — the state, the quantity
 * multiplier, the price, the pill and the one button — so only those are rewritten,
 * with `textContent` and `document.createElement`. No server string is ever
 * concatenated into markup (section 10).
 *
 * THE COPY IN HERE IS A MIRROR, NOT AN ORIGINAL. The counts line, the band subtotal
 * label and the sticky bar's note are printed by `www/splashbox_set.py`
 * (`_counts_line`, `macros.group_band`, `_bar`) and are reproduced word for word so a
 * re-quote and a reload cannot say different things about the same list. Change one
 * and you must change the other.
 */

import { ApiError, METHODS, post } from "./api.js";
import { bag as bagStore, lastList } from "./storage.js";
import { all, empty, icon, one, paintBadge, paintBagLinks, span } from "./nav.js";

/** Matches `www/splashbox_set.py:REMOVED_SEP` — one comma-joined parameter. */
const REMOVED_SEP = ",";

/* ── money, printed exactly the way the server prints it ───────────────────── */

/**
 * 2415 -> "2,415". A port of `splashbox.utils.jinja_helpers.rupees`, digit for
 * digit.
 *
 * Indian grouping is not `toLocaleString('en-IN')` on every engine and it is not
 * `{:,}`: three digits, then TWO at a time, so 1240000 reads "12,40,000". A parent
 * checking a set total against a printed school list reads "1,240,000" as ten times
 * the price. Rounding is `floor(x + 0.5)` — half UP — because that is the rule
 * everywhere else in this app (`pricing.unit_price`, `rupees`), and JavaScript's own
 * `Math.round` agrees with it while Python's `round` does not.
 *
 * This FORMATS a number the server sent. It never produces one.
 */
export function rupees(value) {
	let amount = Number(value);
	if (!isFinite(amount)) amount = 0;

	const whole = Math.floor(Math.abs(amount) + 0.5);
	const sign = amount < 0 && whole ? "-" : "";
	const digits = String(whole);
	if (digits.length <= 3) return sign + digits;

	let head = digits.slice(0, -3);
	const tail = digits.slice(-3);
	const groups = [];
	while (head.length > 2) {
		groups.unshift(head.slice(-2));
		head = head.slice(0, -2);
	}
	if (head) groups.unshift(head);
	groups.push(tail);
	return sign + groups.join(",");
}

/**
 * The markup `jinja_helpers.inr` returns: the rupee sign in its own span so it can
 * leave the mono face, then the grouped digits.
 */
function inr(parent, value, prefix) {
	if (prefix) parent.appendChild(document.createTextNode(prefix));
	parent.appendChild(span("sb-rs", "₹"));
	parent.appendChild(document.createTextNode(rupees(value)));
	return parent;
}

/** `macros.money` — the same figure wrapped in the tabular face. */
function money(value) {
	return inr(span("sb-mono"), value);
}

/** Jinja's `| int`: truncate toward zero, never round. */
function whole(value) {
	const number = Number(value);
	return isFinite(number) ? Math.trunc(number) : 0;
}

/** "1 item" / "2 items" — the plural rule the controller uses. */
function plural(count, word) {
	return count + " " + word + (count === 1 ? "" : "s");
}

/* ── page state ────────────────────────────────────────────────────────────── */

const page = {
	root: null,
	form: null,
	rowsBox: null,
	bundle: "",
	setUrl: "",
	sets: 1,
	maxSets: 200,
	removed: [],
	card: "",
	sequence: 0,
	pending: null
};

/** The current state as this page's own URL — a port of `splashbox_set.py:_href`. */
function setHref(printView) {
	const params = new URLSearchParams();
	params.set("sets", String(page.sets));
	if (page.removed.length) params.set("removed", page.removed.join(REMOVED_SEP));
	if (page.card) params.set("card", page.card);
	if (printView) params.set("print", "1");
	return page.setUrl + "?" + params.toString();
}

/**
 * The same state as a bag URL — `_href("/bag", …, bundle=…)`.
 *
 * Any loose extras the browser was already carrying are carried through. Without
 * that, a parent who added a geometry box on the category shelf and then opened
 * their school's list would find the box quietly gone from the bag, because this
 * page's own state does not know about it.
 */
function bagHref() {
	const params = new URLSearchParams();
	params.set("sets", String(page.sets));
	if (page.removed.length) params.set("removed", page.removed.join(REMOVED_SEP));
	if (page.card) params.set("card", page.card);
	if (page.bundle) params.set("bundle", page.bundle);
	looseRows().forEach(function (row) {
		params.append("loose", row.item_code + ":" + row.qty);
	});
	return "/bag?" + params.toString();
}

/** Loose extras already in this browser's bag, if any. */
function looseRows() {
	const stored = bagStore.get();
	return (stored && stored.loose) || [];
}

/** Keep the hidden inputs true, so a native submit carries the same state. */
function syncFormState() {
	const setsInput = one('input[name="sets"]', page.form);
	if (setsInput) setsInput.value = String(page.sets);

	const removedInput = one("[data-sb-removed]", page.form);
	if (removedInput) removedInput.value = page.removed.join(REMOVED_SEP);

	const cardHidden = one('input[type="hidden"][name="card"]', page.form);
	if (cardHidden) cardHidden.value = page.card;

	const printLink = one("[data-sb-print]");
	if (printLink) printLink.setAttribute("href", setHref(true));
}

/* ── painting one row ──────────────────────────────────────────────────────── */

/** `macros.set_row`'s quantity cell. */
function paintQty(row, line, removed) {
	const cell = one(".sb-qty", row);
	if (!cell) return;
	empty(cell);

	if (removed) {
		cell.appendChild(span("sb-fixed", "—"));
		return;
	}

	// The PRINTED quantity is the set's own — what the school asked for one child to
	// have — while `line_total` already carries the × sets multiplication. That is
	// how the server renders it (`_merge_groups` leaves `row["qty"]` alone), so the
	// multiplier is shown beside it rather than folded into it.
	const qty = whole(row.getAttribute("data-sb-qty"));
	cell.appendChild(span("sb-fixed", "qty " + qty));
	if (page.sets > 1) cell.appendChild(span("sb-mult", "× " + page.sets + " sets"));
}

/** `macros.set_row`'s price cell. */
function paintPrice(row, line, removed) {
	const cell = one(".sb-price", row);
	if (!cell) return;
	empty(cell);

	if (removed) {
		cell.appendChild(money(0));
		return;
	}

	if (!line) return;

	const qty = whole(row.getAttribute("data-sb-qty"));
	const percent = Number(line.percent) || 0;
	const total = line.line_total !== undefined ? line.line_total : line.unit_price;

	if (line.state === "pending" || percent > 0) {
		// `<s>MRP <span class="sb-mono">₹270</span></s>` — the struck figure goes
		// through `money()` and keeps the tabular face, exactly as the macro renders
		// it. A bare `inr()` here would set the old price in the body font.
		const struck = document.createElement("s");
		struck.appendChild(document.createTextNode("MRP "));
		struck.appendChild(money(line.mrp));
		if (qty > 1) struck.appendChild(document.createTextNode(" × " + qty));
		cell.appendChild(struck);
	}

	cell.appendChild(money(total));

	if (line.state === "pending") {
		cell.appendChild(document.createTextNode(" later"));
	} else if (percent > 0) {
		cell.appendChild(span("sb-off", whole(percent) + "% off"));
	}
}

/**
 * `macros.set_row`'s status cell.
 *
 * The pill's words and colour come from `jinja_helpers._PILLS`, and only the three
 * row states can appear here: a quoted line is `billed`, `pending` or `removed`.
 */
const PILLS = {
	billed: ["sb-ok", "In stock"],
	in_stock: ["sb-ok", "In stock"],
	pending: ["sb-pend", "Pending"],
	removed: ["sb-none", "Removed by you"]
};

function paintStatus(row, state, removed) {
	const cell = one(".sb-st", row);
	if (!cell) return;
	empty(cell);

	if (removed) {
		cell.appendChild(span("sb-cap", "Removed by you"));
		return;
	}

	const pill = PILLS[state];
	if (pill) cell.appendChild(span("sb-pill " + pill[0], pill[1]));
	if (state === "pending") {
		cell.appendChild(span("sb-cap", "from the publisher · not charged now"));
	}
}

/**
 * `macros.set_row`'s buttons.
 *
 * The "tell me when it arrives" toggle is MOVED rather than rebuilt: it carries a
 * checked state the customer set and, once bound, a watch that already exists on the
 * server. A row's state can only change here by being removed or restored — stock
 * does not move between two taps — so no row ever has to grow a toggle it did not
 * load with.
 */
function paintActions(row, code, removed) {
	const acts = one(".sb-acts", row);
	if (!acts) return;

	const toggle = one(".sb-tog", acts);
	empty(acts);

	if (removed) {
		acts.appendChild(button("restore", code, "Put it back"));
		return;
	}

	if (toggle) acts.appendChild(toggle);
	acts.appendChild(button("remove", code, "Remove from my set"));
}

function button(name, value, label) {
	const node = document.createElement("button");
	node.className = "sb-lnk";
	node.type = "submit";
	node.name = name;
	node.value = value;
	node.setAttribute("data-sb-act", name);
	node.textContent = label;
	return node;
}

function paintRows(quote) {
	const lines = {};
	(quote.lines || []).forEach(function (line) {
		// Loose extras are not on this page; only the set's own rows are drawn here.
		if (line.source === "bundle" && line.item_code) lines[line.item_code] = line;
	});

	all("[data-sb-row]", page.rowsBox).forEach(function (row) {
		const code = row.getAttribute("data-sb-item");
		const line = lines[code] || null;
		const state = line ? line.state : row.getAttribute("data-sb-state");
		const removed = state === "removed";

		row.setAttribute("data-sb-state", state || "");
		row.classList.toggle("sb-row-pending", state === "pending");
		row.classList.toggle("sb-row-removed", removed);

		paintQty(row, line, removed);
		paintPrice(row, line, removed);
		paintStatus(row, state, removed);
		paintActions(row, code, removed);
	});

	return lines;
}

/* ── painting the bands ────────────────────────────────────────────────────── */

/**
 * Walk `[data-sb-rows]` and hand each band its own total.
 *
 * The bands are flat siblings — a `.sb-gh` header, its rows, a `.sb-subtotal` — so
 * the grouping is reconstructed by walking in order. A band is matched to the
 * quote's group through its rows' `tier_group`, NOT through the heading text: rows
 * are grouped by the TIER's item group, not by the item's own (section 15), and the
 * heading also carries a row count that would have to be parsed back out of it.
 */
function paintBands(lines, quote) {
	const groups = {};
	(quote.groups || []).forEach(function (group) {
		groups[group.item_group] = group;
	});

	let rows = [];
	let subtotal = null;

	function flush() {
		if (!subtotal) return;

		let group = null;
		for (let index = 0; index < rows.length; index += 1) {
			const line = lines[rows[index].getAttribute("data-sb-item")];
			if (line && groups[line.tier_group]) {
				group = groups[line.tier_group];
				break;
			}
		}

		// A band whose every row was removed is dropped from the quote's groups
		// (`_totals` skips removed lines before they reach one), which is not a bug:
		// it comes to nothing and is billed for nothing.
		const amount = group ? group.amount : 0;
		const billed = group ? whole(group.billed_count) : 0;
		const total = rows.length;

		empty(subtotal);
		subtotal.appendChild(
			span("", "Band total" + (billed < total ? " · billed " + billed + " of " + total : ""))
		);
		subtotal.appendChild(money(amount));
	}

	Array.prototype.forEach.call(page.rowsBox.children, function (node) {
		if (node.classList.contains("sb-gh")) {
			// A new band starts here. Anything still accumulated belongs to a band
			// that had no subtotal row, which is nothing to paint.
			rows = [];
			subtotal = null;
		} else if (node.hasAttribute("data-sb-row")) {
			rows.push(node);
		} else if (node.classList.contains("sb-subtotal")) {
			subtotal = node;
			flush();
			subtotal = null;
			rows = [];
		}
	});
}

/* ── painting the ledger ───────────────────────────────────────────────────── */

/** A port of `macros.ledger`. Same rows, same order, same classes. */
function paintLedger(quote) {
	const host = one("[data-sb-ledger]");
	if (!host) return;

	const ledger = document.createElement("div");
	ledger.className = "sb-ledger";

	function line(classes, label, value, minus) {
		const row = document.createElement("div");
		row.className = classes;
		row.appendChild(span("", label));
		row.appendChild(minus ? inr(span("sb-mono"), value, "−") : money(value));
		ledger.appendChild(row);
	}

	const sets = whole(quote.sets);
	line("sb-ln", "MRP total" + (sets > 1 ? " · " + sets + " sets" : ""), quote.mrp_total);

	if (Number(quote.discount_total) > 0) {
		line("sb-ln sb-green", "Discount", quote.discount_total, true);
		(quote.groups || []).forEach(function (group) {
			if (Number(group.discount_total) > 0) {
				line(
					"sb-ln sb-sub",
					group.item_group + " " + whole(group.percent) + "%",
					group.discount_total,
					true
				);
			}
		});
	}

	if (Number(quote.card_amount) > 0) {
		const card = quote.card || {};
		const label =
			"Privilege Card" +
			(card.card_type ? " · " + card.card_type : "") +
			" " +
			whole(quote.card_percent) +
			"%";
		line("sb-ln sb-green", label, quote.card_amount, true);
	}

	const total = document.createElement("div");
	total.className = "sb-ln sb-total";
	const label = span("sb-lbl");
	const mark = document.createElement("mark");
	mark.className = "sb-hl";
	mark.textContent = "You pay";
	label.appendChild(mark);
	if (quote.bulk) {
		label.appendChild(document.createTextNode(" "));
		label.appendChild(span("sb-cap", "· to confirm"));
	}
	total.appendChild(label);
	total.appendChild(money(quote.total_payable));
	ledger.appendChild(total);

	if (Number(quote.pending_total) > 0) {
		const later = document.createElement("div");
		later.className = "sb-ln sb-later";
		later.appendChild(span("sb-lbl", "Pending, pay later"));
		later.appendChild(money(quote.pending_total));
		ledger.appendChild(later);
	}

	empty(host);
	host.appendChild(ledger);
}

/* ── painting the sticky bar and the counts line ───────────────────────────── */

/**
 * A port of `www/splashbox_set.py:_bar`.
 *
 * `total_payable`, never `you_pay_before_card` — they are two different numbers with
 * two different names precisely so that this bar cannot quietly show the pre-card
 * figure while the counter charges the other one (section 4). Pending rows are NOT
 * in it; that is what the note is for.
 */
function paintBar(quote) {
	const bar = one("[data-sb-bar]");
	if (!bar) return;

	const sets = whole(quote.sets) || 1;
	const billed = whole(quote.billable_count);
	const card = quote.card || {};

	let note;
	let label = "Book this set";
	let disabled = false;

	if (!billed) {
		note = "Nothing on this list is selected — put a row back to book it.";
		disabled = true;
	} else if (quote.bulk) {
		note = sets + " sets · we'll call to confirm";
		label = "Reserve " + sets + " sets";
	} else {
		note = plural(sets, "set") + " · " + plural(billed, "item") + " billed";
		if (card.valid) note += " · card applied";
	}

	const amount = one("[data-sb-bar-amount]", bar);
	if (amount) {
		empty(amount);
		// `sticky_bar.html` prints `sb.money(bar.amount)` then the `<small>` note.
		amount.appendChild(money(quote.total_payable));
		const small = document.createElement("small");
		small.setAttribute("data-sb-bar-note", "");
		small.textContent = note;
		amount.appendChild(small);
	}

	paintBarButton(bar, label, disabled);
}

/**
 * The bar's one verb.
 *
 * `sticky_bar.html` renders a `<button disabled>` when there is nothing to book and
 * an `<a href>` when there is, so the element itself has to change as the customer
 * puts the last row back. It is rebuilt rather than mutated, because an anchor with
 * `disabled` on it is still a link and a disabled button with an `href` is still
 * dead.
 */
function paintBarButton(bar, label, disabled) {
	const existing = one("[data-sb-bar-go], .sb-verb", bar);
	if (!existing) return;

	const node = document.createElement(disabled ? "button" : "a");
	node.className = "sb-verb";
	if (disabled) {
		node.type = "button";
		node.disabled = true;
	} else {
		node.setAttribute("href", bagHref());
		node.setAttribute("data-sb-bar-go", "");
	}

	node.appendChild(span("", label));
	const go = span("sb-go");
	go.appendChild(icon("arrow"));
	node.appendChild(go);

	existing.parentNode.replaceChild(node, existing);
}

/** A port of `www/splashbox_set.py:_counts_line`. */
function paintCounts(quote) {
	const host = one("[data-sb-set-count]");
	if (!host) return;

	const total = all("[data-sb-row]", page.rowsBox).length;
	const billed = whole(quote.billable_count);
	const pending = whole(quote.pending_count);
	const removed = whole(quote.removed_count);

	let text = plural(total, "item") + " · " + billed + " in stock, " + pending + " pending";
	if (removed) text += ", " + removed + " removed by you";
	host.textContent = text;
}

/* ── painting the privilege card's answer ──────────────────────────────────── */

/**
 * The one line under the card field, in the two shapes `splashbox-set.html` renders.
 *
 * The message is never composed here: an invalid card's sentence is written by
 * `pricing.card_discount`, which deliberately answers an unknown number and an
 * inactive card with the SAME words so this field cannot be used as a card-number
 * oracle. All this does is print it.
 */
function paintCard(quote) {
	const row = one(".sb-pcrow");
	if (!row) return;

	let host = one("[data-sb-card-msg]");
	const card = quote.card || null;

	if (!card) {
		if (host && host.parentNode) host.parentNode.removeChild(host);
		return;
	}

	if (!host) {
		host = document.createElement("div");
		host.setAttribute("data-sb-card-msg", "");
		row.parentNode.insertBefore(host, row.nextSibling);
	}

	empty(host);

	if (card.valid && Number(quote.card_amount) > 0) {
		host.className = "sb-pcmsg sb-ok-msg";
		host.appendChild(
			document.createTextNode(
				(card.card_type || "Privilege") + " card · " + whole(quote.card_percent) + "% applied · "
			)
		);
		inr(host, quote.card_amount);
		host.appendChild(document.createTextNode(" saved"));
		return;
	}

	host.className = "sb-pcmsg";
	if (!card.message) return;

	const inset = document.createElement("div");
	inset.className = "sb-inset sb-amb";
	inset.appendChild(icon("info"));
	inset.appendChild(span("", card.message));
	host.appendChild(inset);
}

/* ── the re-quote ──────────────────────────────────────────────────────────── */

/** Fall back to what the button would have done with the script switched off. */
function fallback() {
	window.location.assign(setHref(false));
}

function busy(on) {
	if (!page.root) return;
	if (on) page.root.setAttribute("aria-busy", "true");
	else page.root.removeAttribute("aria-busy");
}

/**
 * Ask the server what this list costs now, and repaint everything from the answer.
 *
 * Requests are sequenced and the previous one is aborted: a customer tapping "10"
 * and then "40" must not be shown the ten-set total because it landed second.
 */
function requote() {
	syncFormState();
	busy(true);

	const ticket = (page.sequence += 1);
	if (page.pending) page.pending.abort();
	const controller = typeof AbortController === "function" ? new AbortController() : null;
	page.pending = controller;

	return post(
		METHODS.quote,
		{
			bundle: page.bundle,
			sets: page.sets,
			removed: page.removed,
			card: page.card
		},
		{ signal: controller ? controller.signal : undefined }
	)
		.then(function (quote) {
			if (ticket !== page.sequence) return;
			page.pending = null;
			busy(false);

			// `sets` is clamped to `Settings.max_sets` server-side, so the answer is
			// the authority on how many sets this actually is.
			page.sets = whole(quote.sets) || page.sets;
			markChips(page.sets);
			syncFormState();

			const lines = paintRows(quote);
			paintBands(lines, quote);
			paintLedger(quote);
			paintCard(quote);
			paintCounts(quote);
			paintBar(quote);
			rememberBag(quote);

			// Anything else on the page that renders a total — the UPI block on the
			// bag shares this event — hears about it here rather than reaching into
			// this module's state.
			document.dispatchEvent(new CustomEvent("sb:quote", { detail: quote }));
		})
		.catch(function (error) {
			// A request this one superseded rejects with an abort; its ticket is
			// already stale, so it is dropped here before anything is decided from
			// it. Reaching past this line means the CURRENT request failed.
			if (ticket !== page.sequence) return;
			page.pending = null;
			busy(false);
			if (!(error instanceof ApiError)) throw error;
			fallback();
		});
}

/**
 * Record what the bar is about to hand to `/bag`, so the badge and every Bag link
 * on the next page agree with the list the customer just built.
 *
 * Only ever reached from a deliberate change — a row dropped, a chip tapped, a card
 * applied — never from simply opening the page. Looking at a list is not carrying
 * it, and a badge that lit up on page load would be the storefront putting things in
 * a parent's bag on their behalf.
 */
function rememberBag(quote) {
	const billed = whole(quote.billable_count);
	const loose = looseRows();

	if (!billed && !loose.length) {
		bagStore.clear();
	} else {
		bagStore.set({
			href: bagHref(),
			count: billed + loose.length,
			total: quote.total_payable,
			loose: loose
		});
	}
	paintBadge();
	paintBagLinks();
}

/* ── controls ──────────────────────────────────────────────────────────────── */

function markChips(count) {
	all("[data-sb-chip]").forEach(function (chip) {
		const on = whole(chip.getAttribute("data-sb-chip")) === count;
		chip.classList.toggle("sb-sel", on);
		chip.setAttribute("aria-pressed", on ? "true" : "false");
	});
	const other = one("#sb-sets-other");
	if (other) other.value = String(count);
}

function setCount(count) {
	const wanted = whole(count);
	if (!wanted) return;
	page.sets = Math.max(1, Math.min(page.maxSets, wanted));
	markChips(page.sets);
	requote();
}

function remove(code) {
	if (!code || page.removed.indexOf(code) !== -1) return;
	page.removed.push(code);
	requote();
}

function restore(code) {
	const at = page.removed.indexOf(code);
	if (at === -1) return;
	page.removed.splice(at, 1);
	requote();
}

function applyCard() {
	const input = one("[data-sb-card]");
	page.card = input ? String(input.value || "").trim() : "";
	requote();
}

function onClick(event) {
	const control = event.target.closest("button");
	if (!control || control.type === "button") return;

	const name = control.getAttribute("name") || "";
	const value = control.getAttribute("value") || "";

	if (name === "remove") {
		event.preventDefault();
		remove(value);
	} else if (name === "restore") {
		event.preventDefault();
		restore(value);
	} else if (name === "sets_pick") {
		event.preventDefault();
		if (value === "other") {
			const other = one("#sb-sets-other");
			setCount(other ? other.value : page.sets);
		} else {
			setCount(value);
		}
	} else if (name === "apply_card") {
		event.preventDefault();
		applyCard();
	}
}

/**
 * Enter inside one of the form's text fields.
 *
 * Without this the browser's implicit submission picks the FIRST submit button in
 * the form, which on this page is the first row's "Remove from my set" — so pressing
 * Enter after typing a card number would drop a book off the list. Intercepting the
 * form's own submit event turns it into what the customer meant, which depends on
 * which field they were standing in.
 */
function onSubmit(event) {
	event.preventDefault();
	const focused = document.activeElement;
	if (focused && focused.id === "sb-sets-other") setCount(focused.value);
	else applyCard();
}

/* ── the pending-row watch toggle ──────────────────────────────────────────── */

/**
 * "Tell me on WhatsApp when it arrives".
 *
 * The checkbox is inside the set form, but the controller does not read it — a watch
 * is a row in `Splashbox Item Watch` owned by a signed-in customer, not a query
 * parameter. `api.watch.add_watch` is `allow_guest=False` on purpose (a watch has to
 * belong to somebody to be delivered to them), so a signed-out customer is offered
 * the way in rather than being told nothing happened.
 *
 * `frappe-session-status` on `<body>` is frappe's own server-rendered answer to "is
 * anyone signed in" (frappe/templates/base.html), which is why this does not have to
 * ask.
 */
function signedIn() {
	return document.body.getAttribute("frappe-session-status") === "logged-in";
}

function watches() {
	if (!page.form) return;

	page.form.addEventListener("change", function (event) {
		const box = event.target;
		if (!box || box.getAttribute("data-sb-act") !== "watch") return;

		const code = box.value;
		const label = box.closest(".sb-tog");

		if (!signedIn()) {
			box.checked = false;
			offerSignIn(label);
			return;
		}

		const method = box.checked ? METHODS.addWatch : METHODS.removeWatch;
		post(method, { item_code: code, product_bundle: page.bundle }).catch(function (error) {
			if (!(error instanceof ApiError)) throw error;
			// The toggle must never claim a message will arrive when the server did
			// not agree to send one.
			box.checked = !box.checked;
			if (error.code === "forbidden") offerSignIn(label);
			// "Your account has no mobile number" is `api.watch`'s own sentence for a
			// staff account browsing the shop, and it is worth reading.
			else if (error.fromEnvelope) watchNote(label, error.message, false);
		});
	});
}

function offerSignIn(label) {
	watchNote(label, "We'll message you when it arrives — ", true);
}

/**
 * One line under a watch toggle that could not be honoured.
 *
 * Replaces whatever was there rather than stacking: a customer tapping a toggle
 * twice should not end up with two apologies. The note lives inside `.sb-acts`, so
 * the next re-quote's `paintActions` clears it, which is the right lifetime — by
 * then the customer has either signed in or moved on.
 */
function watchNote(label, text, signIn) {
	if (!label || !label.parentNode) return;

	const existing = one(".sb-watch-note", label.parentNode);
	if (existing) existing.parentNode.removeChild(existing);

	const note = span("sb-cap sb-watch-note", text);
	if (signIn) {
		const link = document.createElement("a");
		link.setAttribute(
			"href",
			"/account/login?redirect-to=" +
				encodeURIComponent(window.location.pathname + window.location.search)
		);
		link.textContent = "sign in";
		note.appendChild(link);
	}
	label.parentNode.insertBefore(note, label.nextSibling);
}

/* ── the card scanner ──────────────────────────────────────────────────────── */

/**
 * Read the barcode on the back of a Privilege Card with the phone's camera.
 *
 * `splashbox-set.html` ships this button `hidden` and says why: a scan button that
 * does nothing without JavaScript is worse than no button. It is revealed here only
 * when the browser really can scan — `BarcodeDetector` is Chrome/Edge on Android and
 * nothing else today — so a customer is never shown a control that will ask for
 * their camera and then fail.
 *
 * The overlay carries its own layout in inline styles rather than relying on a class
 * the stylesheet may not have: a viewfinder that renders as an unpositioned video
 * halfway down the page is a worse failure than no scanner at all.
 */
function scanner() {
	const trigger = one("[data-sb-card-scan]");
	const input = one("[data-sb-card]");
	if (!trigger || !input) return;

	const Detector = window.BarcodeDetector;
	const media = navigator.mediaDevices;
	if (typeof Detector !== "function" || !media || typeof media.getUserMedia !== "function") return;
	// getUserMedia is refused outright off a secure origin, so the button would only
	// ever produce a permission error.
	if (!window.isSecureContext) return;

	trigger.removeAttribute("hidden");
	trigger.addEventListener("click", function (event) {
		event.preventDefault();
		openScanner(Detector, media, input);
	});
}

function openScanner(Detector, media, input) {
	let detector;
	try {
		detector = new Detector({ formats: ["code_128", "code_39", "qr_code", "ean_13"] });
	} catch (error) {
		try {
			detector = new Detector();
		} catch (again) {
			return;
		}
	}

	const overlay = document.createElement("div");
	overlay.className = "sb-scan";
	overlay.setAttribute("role", "dialog");
	overlay.setAttribute("aria-label", "Scan the card");
	overlay.style.cssText =
		"position:fixed;inset:0;z-index:9999;display:flex;flex-direction:column;" +
		"align-items:center;justify-content:center;gap:16px;background:rgba(14,18,32,.92);padding:16px;";

	const video = document.createElement("video");
	video.setAttribute("playsinline", "");
	video.setAttribute("muted", "");
	video.muted = true;
	video.autoplay = true;
	video.style.cssText = "width:min(560px,100%);max-height:70vh;border-radius:16px;background:#000;";

	const hint = document.createElement("p");
	hint.textContent = "Hold the barcode on the back of the card inside the frame.";
	hint.style.cssText = "color:#fff;font-size:16px;text-align:center;margin:0;";

	const close = document.createElement("button");
	close.type = "button";
	close.className = "sb-pill-btn sb-sec";
	close.textContent = "Cancel";
	close.style.cssText = "min-height:44px;";

	overlay.appendChild(video);
	overlay.appendChild(hint);
	overlay.appendChild(close);
	document.body.appendChild(overlay);
	close.focus();

	let stream = null;
	let timer = null;
	let closed = false;

	function stop() {
		if (closed) return;
		closed = true;
		if (timer) clearInterval(timer);
		if (stream) {
			stream.getTracks().forEach(function (track) {
				track.stop();
			});
		}
		if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
	}

	close.addEventListener("click", stop);
	overlay.addEventListener("click", function (event) {
		if (event.target === overlay) stop();
	});
	document.addEventListener("keydown", function onKey(event) {
		if (event.key !== "Escape") return;
		document.removeEventListener("keydown", onKey);
		stop();
	});

	media
		.getUserMedia({ video: { facingMode: "environment" } })
		.then(function (granted) {
			if (closed) {
				granted.getTracks().forEach(function (track) {
					track.stop();
				});
				return;
			}
			stream = granted;
			video.srcObject = granted;
			timer = setInterval(function () {
				detector
					.detect(video)
					.then(function (codes) {
						if (!codes || !codes.length) return;
						const value = String(codes[0].rawValue || "").trim();
						if (!value) return;
						stop();
						input.value = value.slice(0, 60);
						applyCard();
					})
					.catch(function () {
						/* a frame that could not be read is the normal case */
					});
			}, 250);
		})
		.catch(function () {
			hint.textContent = "We could not open the camera. Type the number on the card instead.";
		});
}

/* ── quantity steppers ─────────────────────────────────────────────────────── */

/**
 * Turn the `qty_stepper` macro's bare number input into the design's two round
 * buttons.
 *
 * It is a real `<input type="number">` first so it works and is keyboard-accessible
 * with no JavaScript at all (the macro says so), which is also why the input is kept
 * here rather than replaced: the buttons drive it, and both fire the events any other
 * listener is bound to.
 *
 * This lives with the set page for the reason `macros.set_row` has NO stepper: a
 * set's quantities belong to the set (section 11), so the only place a customer may
 * choose a quantity is a loose item on its own page — which is what this upgrades.
 */
function steppers() {
	all("[data-sb-step]").forEach(function (host) {
		const input = one('input[type="number"]', host);
		if (!input || one(".sb-stepbtn", host)) return;

		const min = parseInt(input.getAttribute("min"), 10) || 1;
		const max = parseInt(input.getAttribute("max"), 10) || 999;

		function nudge(by) {
			const now = parseInt(input.value, 10) || min;
			const next = Math.max(min, Math.min(max, now + by));
			if (next === now) return;
			input.value = String(next);
			input.dispatchEvent(new Event("input", { bubbles: true }));
			input.dispatchEvent(new Event("change", { bubbles: true }));
		}

		host.insertBefore(stepButton("−", "One fewer", -1, nudge), input);
		host.appendChild(stepButton("+", "One more", 1, nudge));
	});
}

function stepButton(glyph, label, by, nudge) {
	const node = document.createElement("button");
	node.type = "button";
	node.className = "sb-stepbtn";
	node.textContent = glyph;
	node.setAttribute("aria-label", label);
	node.addEventListener("click", function (event) {
		event.preventDefault();
		nudge(by);
	});
	return node;
}

/* ── boot ──────────────────────────────────────────────────────────────────── */

/**
 * Remember this list, so `/` can offer it back on the next visit.
 *
 * Nothing identifying goes in: the set's own public URL, its code and the counts
 * line the page already prints. See `storage.js` on what may never be kept.
 */
function remember() {
	const heading = one(".sb-sethead h1");
	const code = one(".sb-codepill");
	const counts = one("[data-sb-set-count]");
	lastList.set({
		href: window.location.pathname,
		title: heading ? heading.textContent : "",
		code: code ? code.textContent : "",
		meta: counts ? counts.textContent : ""
	});
}

export function init() {
	page.root = one("[data-sb-set]");
	page.form = one("#sb-set");
	page.rowsBox = one("[data-sb-rows]");

	// The steppers are the one thing here that lives on another page.
	steppers();

	if (!page.root || !page.form || !page.rowsBox) return;

	page.bundle = page.root.getAttribute("data-sb-set") || "";
	page.setUrl = page.form.getAttribute("action") || window.location.pathname;

	const setsInput = one('input[name="sets"]', page.form);
	page.sets = whole(setsInput && setsInput.value) || 1;

	const other = one("#sb-sets-other");
	page.maxSets = (other && parseInt(other.getAttribute("max"), 10)) || 200;

	const removedInput = one("[data-sb-removed]", page.form);
	page.removed = String((removedInput && removedInput.value) || "")
		.split(REMOVED_SEP)
		.map(function (code) {
			return code.trim();
		})
		.filter(Boolean);

	const cardInput = one("[data-sb-card]");
	page.card = cardInput ? String(cardInput.value || "").trim() : "";

	page.form.addEventListener("click", onClick);
	page.form.addEventListener("submit", onSubmit);
	watches();
	scanner();
	remember();
}
